import { TIME_AND_EXPENSE_ENTRY_TYPE } from '~/modules/common/enums/TimeAndExpenseEntryType';
import { TimeEntryMetadataKey } from '~/types';
import { BILLING_STATUS_VALUES, NO_TASK_OPTION } from './constants';

export const getBillingMetadata = billingStatusValue => {
  const metadata = {
    customMetadataKeysToRemove: [],
    customMetadataToAddOrUpdate: []
  };

  if (
    !billingStatusValue ||
    billingStatusValue === TIME_AND_EXPENSE_ENTRY_TYPE.NON_BILLABLE
  ) {
    metadata.customMetadataKeysToRemove = [TimeEntryMetadataKey.BillingRate];
    metadata.customMetadataToAddOrUpdate = [
      {
        key: TimeEntryMetadataKey.IsBillable,
        value: { bool: false }
      }
    ];

    return metadata;
  }

  if (billingStatusValue === BILLING_STATUS_VALUES.NO_CHANGE) return metadata;
  if (billingStatusValue === TIME_AND_EXPENSE_ENTRY_TYPE.BILLABLE) {
    metadata.customMetadataKeysToRemove = [TimeEntryMetadataKey.BillingRate];
    metadata.customMetadataToAddOrUpdate = [
      {
        key: TimeEntryMetadataKey.IsBillable,
        value: { bool: true }
      }
    ];

    return metadata;
  }

  metadata.customMetadataToAddOrUpdate = [
    {
      key: TimeEntryMetadataKey.BillingRate,
      value: { uri: billingStatusValue }
    },
    {
      key: TimeEntryMetadataKey.IsBillable,
      value: { bool: true }
    }
  ];

  return metadata;
};

export const getEffectiveEntryType = ({
  projectEntryTypeUri,
  taskEntryTypeUri,
  task
}) => {
  if (task?.id === NO_TASK_OPTION.id) return projectEntryTypeUri;

  return taskEntryTypeUri || projectEntryTypeUri;
};

const createNoChangeOption = formatMessage => ({
  id: BILLING_STATUS_VALUES.NO_CHANGE,
  displayText: formatMessage({ id: 'moveTimeDialog.noChange' }),
  description: formatMessage({
    id: 'moveTimeDialog.noChangeDescription'
  })
});

const createOption = ({ id, formatMessage }) => ({
  id,
  displayText: formatMessage({ id: `timeAndExpenseEntryOptions.${id}` })
});

export const createBillingStatusOptionBuilders = (
  formatMessage,
  billingRates
) => {
  const createStandardOption = id => createOption({ id, formatMessage });

  return {
    createNoChangeOption: () => createNoChangeOption(formatMessage),
    createNonBillableOption: () =>
      createStandardOption(TIME_AND_EXPENSE_ENTRY_TYPE.NON_BILLABLE),
    createBillableOption: () =>
      createStandardOption(TIME_AND_EXPENSE_ENTRY_TYPE.BILLABLE),
    createProjectRateOption: () => ({
      id: 'urn:replicon:project-specific-billing-rate',
      displayText: formatMessage({
        id: 'moveTimeDialog.projectRate'
      }),
      description: formatMessage({
        id: 'moveTimeDialog.billable'
      }),
      isBillingRate: true
    }),
    createBillingRateOptions: () =>
      billingRates.map(({ id, displayText }) => ({
        id,
        displayText,
        description: formatMessage({
          id: 'moveTimeDialog.billable'
        }),
        isBillingRate: true
      }))
  };
};

export const getBillingOptionsForNonBillable = optionBuilders => [
  optionBuilders.createNonBillableOption()
];

export const getBillingOptionsForFixedBid = (
  optionBuilders,
  includeNoChangeOption
) => {
  const options = [];

  if (includeNoChangeOption)
    options.push(optionBuilders.createNoChangeOption());

  options.push(optionBuilders.createNonBillableOption());
  options.push(optionBuilders.createProjectRateOption());

  return options;
};

export const getBillingOptionsForTimeAndMaterial = (
  optionBuilders,
  effectiveEntryType,
  includeNoChangeOption
) => {
  if (effectiveEntryType === TIME_AND_EXPENSE_ENTRY_TYPE.NON_BILLABLE)
    return [optionBuilders.createNonBillableOption()];

  if (
    effectiveEntryType === TIME_AND_EXPENSE_ENTRY_TYPE.BILLABLE_AND_NON_BILLABLE
  ) {
    const options = [];

    if (includeNoChangeOption)
      options.push(optionBuilders.createNoChangeOption());

    options.push(optionBuilders.createNonBillableOption());
    options.push(...optionBuilders.createBillingRateOptions());

    return options;
  }

  if (effectiveEntryType === TIME_AND_EXPENSE_ENTRY_TYPE.BILLABLE) {
    const options = [];

    if (includeNoChangeOption)
      options.push(optionBuilders.createNoChangeOption());

    options.push(...optionBuilders.createBillingRateOptions());

    return options;
  }

  return [];
};

export const getBillingOptionsForBillingContract = (
  optionBuilders,
  effectiveEntryType,
  includeNoChangeOption
) => {
  if (effectiveEntryType === TIME_AND_EXPENSE_ENTRY_TYPE.NON_BILLABLE)
    return [optionBuilders.createNonBillableOption()];

  if (
    effectiveEntryType === TIME_AND_EXPENSE_ENTRY_TYPE.BILLABLE_AND_NON_BILLABLE
  ) {
    const options = [];

    if (includeNoChangeOption)
      options.push(optionBuilders.createNoChangeOption());

    options.push(optionBuilders.createNonBillableOption());
    options.push(optionBuilders.createBillableOption());

    return options;
  }

  if (effectiveEntryType === TIME_AND_EXPENSE_ENTRY_TYPE.BILLABLE) {
    const options = [];

    if (includeNoChangeOption)
      options.push(optionBuilders.createNoChangeOption());

    options.push(optionBuilders.createBillableOption());

    return options;
  }

  return [];
};

export const formatBillingStatusDisplayText = (
  billingStatus,
  formatMessage
) => {
  if (!billingStatus) return '';

  if (billingStatus.id === BILLING_STATUS_VALUES.NO_CHANGE)
    return formatMessage({ id: 'moveTimeDialog.noChangeWithDescription' });

  if (billingStatus.isBillingRate)
    return formatMessage(
      { id: 'moveTimeDialog.billableWithRate' },
      { rate: billingStatus.displayText }
    );

  return billingStatus.displayText;
};
